import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CircleNotch, ChartLine, Table, ChartBar, WarningCircle, ArrowUp, ArrowDown, Equals, TrendUp, TrendDown, Info, MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

const DIFF_COLORS = {
  b2_b1: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700", bar: "#3b82f6", line: "#2563eb", barLight: "#93c5fd" },
  b3_b2: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", bar: "#10b981", line: "#059669", barLight: "#6ee7b7" },
  b4_b3: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", bar: "#f59e0b", line: "#d97706", barLight: "#fcd34d" },
  b5_b4: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", bar: "#ef4444", line: "#dc2626", barLight: "#fca5a5" },
  ls2_ls1: { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-700", bar: "#8b5cf6", line: "#7c3aed", barLight: "#c4b5fd" },
};

const SEV_STYLES = {
  high: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", icon: WarningCircle },
  medium: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", icon: TrendDown },
  low: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-600", icon: Info },
  info: { bg: "bg-indigo-50", border: "border-indigo-300", text: "text-indigo-700", icon: Info },
};

// Numbered Block wrapper — matches visual convention used in W3/W7/W14/W24/W31
function Block({ n, title, subtitle, children, testId }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={testId || `w59-block-${n}`}>
      <header className="bg-slate-800 text-white px-4 py-2.5">
        <h3 className="font-bold text-sm tracking-tight">
          <span className="inline-block px-2 py-0.5 bg-indigo-500 text-white rounded mr-2 text-[11px] font-black">Block {n}</span>
          {title}
        </h3>
        {subtitle && <p className="text-[11px] text-white/70 mt-0.5">{subtitle}</p>}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function WindowBallDiff() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [twinData, setTwinData] = useState(null);
  const [twinLoading, setTwinLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("table");
  const contentRef = useRef(null);

  const fetchMain = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    axios.get(`${API}/api/analysis/ball-diff-2026`)
      .then(res => setData(res.data))
      .catch(() => { if (!silent) setData(null); })
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  const fetchTwin = useCallback(() => {
    axios.get(`${API}/api/analysis/twin-year`)
      .then(res => setTwinData(res.data))
      .catch(() => setTwinData(null));
  }, []);

  useEffect(() => { fetchMain(); }, [fetchMain]);

  // Auto-update on draw addition (same-tab + cross-tab)
  useEffect(() => {
    const onEvt = () => { fetchMain(true); if (twinData) fetchTwin(); };
    const onStorage = (e) => { if (e.key === 'euromillions:last_draw_update') onEvt(); };
    const onVis = () => { if (document.visibilityState === 'visible') fetchMain(true); };
    window.addEventListener('euromillions:draws-updated', onEvt);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVis);
    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') onEvt(); };
      }
    } catch {}
    const id = setInterval(() => fetchMain(true), 30000);
    return () => {
      window.removeEventListener('euromillions:draws-updated', onEvt);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVis);
      if (bc) bc.close();
      clearInterval(id);
    };
  }, [fetchMain, fetchTwin, twinData]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === "twin" && !twinData && !twinLoading) {
      setTwinLoading(true);
      axios.get(`${API}/api/analysis/twin-year`)
        .then(res => setTwinData(res.data))
        .catch(() => setTwinData(null))
        .finally(() => setTwinLoading(false));
    }
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );

  if (!data || data.error) return (
    <div className="min-h-screen bg-gray-50 p-6">
      <button onClick={() => navigate("/")} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft size={18} /> Back
      </button>
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center text-red-700">No 2026 draws found.</div>
    </div>
  );

  const { draws, diff_keys, stats_2026, stats_hist, frequency_2026, frequency_hist, trends_2026, deviation, diff_sum_2026, diff_sum_hist, observations, due_analysis } = data;

  const tabs = [
    { id: "table", label: "Draw Table", icon: Table },
    { id: "trends", label: "Trends", icon: ChartLine },
    { id: "due", label: "What's Due?", icon: TrendUp },
    { id: "twin", label: "Twin Year", icon: MagnifyingGlass },
    { id: "comparison", label: "2026 vs Historical", icon: ChartBar },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ball-diff-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-900" data-testid="back-button">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">W59: Analysis B2-B1</h1>
              <p className="text-xs text-gray-500">
                2026 ({data.total_draws_2026} draws) vs Historical {data.hist_range} ({data.total_draws_hist} draws)
              </p>
            </div>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => switchTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold transition-all ${activeTab === t.id ? "bg-indigo-600 text-white shadow-md" : "text-gray-500 hover:text-gray-700 hover:bg-white"}`}
                data-testid={`tab-${t.id}`}
              >
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 space-y-5">


        <WindowNarrative windowId="W59" {...NARRATIVES.W59} className="mb-5" />
        {/* Block 1 — Deviation Snapshot */}
        <Block
          n={1}
          title="Deviation Snapshot — 2026 Gap Averages vs Historical"
          subtitle="Live gap averages (B2-B1, B3-B2, B4-B3, B5-B4, LS2-LS1) vs 22-year history. Red signal = significant deviation (|Z| ≥ 1.0)."
          testId="w59-block-1"
        >
          <div className="grid grid-cols-6 gap-3">
            {diff_keys.map(dk => {
              const dev = deviation[dk.key] || {};
              const s26 = stats_2026[dk.key] || {};
              const sh = stats_hist[dk.key] || {};
              const c = DIFF_COLORS[dk.key];
              const zAbs = Math.abs(dev.z_score || 0);
              const signalColor = zAbs >= 1.0 ? "text-red-600" : zAbs >= 0.5 ? "text-amber-600" : "text-green-600";
              const SignalIcon = (dev.diff || 0) > 0 ? ArrowUp : (dev.diff || 0) < 0 ? ArrowDown : Equals;
              return (
                <div key={dk.key} className={`${c.bg} border ${c.border} rounded-lg p-2.5`} data-testid={`deviation-${dk.key}`}>
                  <div className={`text-[10px] font-bold ${c.text} uppercase tracking-wider flex items-center justify-between`}>
                    <span>{dk.label}</span>
                    <span className={`flex items-center gap-0.5 ${signalColor}`}><SignalIcon size={9} weight="bold" /> {dev.signal || "N/A"}</span>
                  </div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-mono font-bold text-lg">{s26.avg ?? "-"}</span>
                    <span className="font-mono font-bold text-lg text-gray-400">vs {sh.avg ?? "-"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Block>

        {/* Tab Content — each tab renders its own Block(s) starting at Block 2 */}
        <div ref={contentRef}>
          {activeTab === "due" && <DueAnalysisView dueAnalysis={due_analysis} diffKeys={diff_keys} />}
          {activeTab === "twin" && <TwinYearView data={twinData} loading={twinLoading} />}
          {activeTab === "table" && <DrawTable draws={draws} diffKeys={diff_keys} />}
          {activeTab === "comparison" && <ComparisonView stats2026={stats_2026} statsHist={stats_hist} deviation={deviation} diffKeys={diff_keys} />}
          {activeTab === "trends" && <TrendsView trends={trends_2026} diffKeys={diff_keys} statsHist={stats_hist} />}
        </div>
      </main>
    </div>
  );
}

function DueAnalysisView({ dueAnalysis, diffKeys }) {
  if (!dueAnalysis) return null;

  const STATUS_STYLES = {
    OVERDUE: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", bar: "#ef4444" },
    DUE: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-300", bar: "#f59e0b" },
    NORMAL: { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200", bar: "#9ca3af" },
    SURPLUS: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", bar: "#3b82f6" },
    RARE: { bg: "bg-gray-50", text: "text-gray-400", border: "border-gray-100", bar: "#e5e7eb" },
  };

  return (
    <div className="space-y-5" data-testid="due-analysis-view">
      {/* Block 2 — Due Summary */}
      <Block
        n={2}
        title="Due Summary — What's Overdue & What's in Surplus"
        subtitle="Per ball difference: historical % vs 2026 %. Negative gap = underrepresented = DUE/OVERDUE; positive = SURPLUS."
        testId="w59-block-2"
      >
        <div className="grid grid-cols-5 gap-3">
          {diffKeys.map(dk => {
            const da = dueAnalysis[dk.key];
            if (!da) return null;
            const c = DIFF_COLORS[dk.key];
            const overdue = da.overdue || [];
            const surplus = da.surplus || [];
            return (
              <div key={dk.key} className={`${c.bg} border ${c.border} rounded-lg p-3`}>
                <div className={`text-xs font-bold ${c.text} uppercase tracking-wider mb-2`}>{dk.label}</div>
                {overdue.length > 0 ? (
                  <div className="space-y-1">
                    {overdue.slice(0, 4).map((o, i) => (
                      <div key={i} className={`flex items-center justify-between px-2 py-1 rounded text-[10px] font-bold ${STATUS_STYLES[o.status].bg} ${STATUS_STYLES[o.status].text} border ${STATUS_STYLES[o.status].border}`}>
                        <span>Gap {o.value}</span>
                        <span>{o.y2026_pct}% vs {o.hist_pct}%</span>
                        <span className="font-black">{o.gap_pp > 0 ? "+" : ""}{o.gap_pp}pp</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[10px] text-gray-400 italic">No overdue gaps</div>
                )}
                {surplus.length > 0 && (
                  <div className="mt-2 border-t border-gray-200 pt-1.5">
                    <div className="text-[9px] text-gray-400 uppercase font-bold mb-1">Surplus</div>
                    {surplus.slice(0, 2).map((s, i) => (
                      <div key={i} className="flex items-center justify-between px-2 py-0.5 text-[9px] text-blue-600">
                        <span>Gap {s.value}</span>
                        <span>{s.y2026_pct}% vs {s.hist_pct}%</span>
                        <span className="font-bold">+{s.gap_pp}pp</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Block>

      {/* Block 3 — Per-Diff Detail Tables */}
      <Block
        n={3}
        title="Per-Diff Detail — Gap Distribution & Historical Comparison"
        subtitle="For each adjacent-ball difference, see every gap value with histogram, 2026 count, historical %, and status (OVERDUE / DUE / NORMAL / SURPLUS / RARE)."
        testId="w59-block-3"
      >
        <div className="space-y-5">
          {diffKeys.map(dk => {
            const da = dueAnalysis[dk.key];
            if (!da || !da.entries) return null;
            const c = DIFF_COLORS[dk.key];
            const entries = da.entries.filter(e => e.hist_pct >= 1.0 || e.y2026_count > 0);
            const maxPct = Math.max(...entries.map(e => Math.max(e.hist_pct, e.y2026_pct)), 1);
            const barH = 120;

            return (
          <div key={dk.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`px-4 py-2 ${c.bg} border-b ${c.border} flex items-center justify-between`}>
              <span className={`font-bold text-sm ${c.text}`}>{dk.label} — Full Comparison</span>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded" style={{ backgroundColor: c.bar }} /> 2026</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 rounded" style={{ backgroundColor: c.barLight }} /> Historical</span>
              </div>
            </div>
            <div className="p-4">
              {/* Bar chart */}
              <div className="flex items-end gap-[2px]" style={{ minHeight: barH + 50 }}>
                {entries.map((e, i) => {
                  const h26 = Math.round(Math.max((e.y2026_pct / maxPct) * barH, e.y2026_count > 0 ? 4 : 0));
                  const hh = Math.round(Math.max((e.hist_pct / maxPct) * barH, e.hist_count > 0 ? 4 : 0));
                  const st = STATUS_STYLES[e.status] || STATUS_STYLES.NORMAL;
                  return (
                    <div key={i} className="flex flex-col items-center group relative" style={{ flex: "1 1 0", minWidth: 16 }}>
                      <div className="absolute -top-12 opacity-0 group-hover:opacity-100 bg-gray-800 text-white text-[8px] px-2 py-1 rounded whitespace-nowrap z-10">
                        <div className="font-bold">Gap: {e.value} — {e.status}</div>
                        <div>2026: {e.y2026_count}x ({e.y2026_pct}%)</div>
                        <div>Hist: {e.hist_count}x ({e.hist_pct}%)</div>
                        <div className={e.gap_pp < 0 ? "text-red-300" : "text-green-300"}>{e.gap_pp > 0 ? "+" : ""}{e.gap_pp}pp</div>
                      </div>
                      <div className="flex gap-[1px] items-end w-full">
                        <div className="flex-1 flex flex-col items-center">
                          {e.y2026_count > 0 && <div className="text-[6px] font-mono text-gray-600 mb-0.5">{e.y2026_pct}%</div>}
                          <div className="w-full rounded-t" style={{ height: h26, backgroundColor: c.bar }} />
                        </div>
                        <div className="flex-1 flex flex-col items-center">
                          {e.hist_count > 0 && <div className="text-[6px] font-mono text-gray-400 mb-0.5">{e.hist_pct}%</div>}
                          <div className="w-full rounded-t" style={{ height: hh, backgroundColor: c.barLight }} />
                        </div>
                      </div>
                      <div className={`text-[8px] font-mono mt-0.5 font-bold ${e.status === "OVERDUE" ? "text-red-600" : e.status === "DUE" ? "text-amber-600" : "text-gray-500"}`}>
                        {e.value}
                      </div>
                      {e.status === "OVERDUE" && <div className="text-[6px] font-black text-red-500">DUE!</div>}
                    </div>
                  );
                })}
              </div>

              {/* Table view */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-2 py-1 text-left font-bold text-gray-500">Gap</th>
                      <th className="px-2 py-1 text-right font-bold text-gray-500">Hist Count</th>
                      <th className="px-2 py-1 text-right font-bold text-gray-500">Hist %</th>
                      <th className="px-2 py-1 text-right font-bold text-gray-500">2026 Count</th>
                      <th className="px-2 py-1 text-right font-bold text-gray-500">2026 %</th>
                      <th className="px-2 py-1 text-right font-bold text-gray-500">Gap (pp)</th>
                      <th className="px-2 py-1 text-center font-bold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => {
                      const st = STATUS_STYLES[e.status] || STATUS_STYLES.NORMAL;
                      return (
                        <tr key={i} className={`border-b border-gray-100 ${st.bg}`}>
                          <td className="px-2 py-1 font-mono font-bold">{e.value}</td>
                          <td className="px-2 py-1 text-right font-mono">{e.hist_count}</td>
                          <td className="px-2 py-1 text-right font-mono">{e.hist_pct}%</td>
                          <td className="px-2 py-1 text-right font-mono font-bold">{e.y2026_count}</td>
                          <td className="px-2 py-1 text-right font-mono font-bold">{e.y2026_pct}%</td>
                          <td className={`px-2 py-1 text-right font-mono font-bold ${e.gap_pp < -3 ? "text-red-600" : e.gap_pp > 3 ? "text-blue-600" : "text-gray-500"}`}>
                            {e.gap_pp > 0 ? "+" : ""}{e.gap_pp}
                          </td>
                          <td className="px-2 py-1 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${st.bg} ${st.text} border ${st.border}`}>
                              {e.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
          })}
        </div>
      </Block>
    </div>
  );
}

function DrawTable({ draws, diffKeys }) {
  // Show most recent draws first (descending by draw_number)
  const sortedDraws = [...(draws || [])].sort((a, b) => (b.draw_number || 0) - (a.draw_number || 0));
  return (
    <Block
      n={2}
      title="Draw Table — All 2026 Draws with Adjacent-Ball Diffs"
      subtitle="Each row: balls, lucky stars, plus raw adjacent differences (B2-B1, B3-B2, B4-B3, B5-B4, LS2-LS1) per draw. Sorted newest → oldest."
      testId="w59-block-2"
    >
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="draw-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">#</th>
              <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Date</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">B1</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">B2</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">B3</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">B4</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">B5</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">LS1</th>
              <th className="px-3 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">LS2</th>
              {diffKeys.map(dk => (
                <th key={dk.key} className={`px-3 py-2 text-center text-[10px] font-bold uppercase ${DIFF_COLORS[dk.key]?.text || "text-gray-500"}`}>
                  {dk.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedDraws.map((d, i) => (
              <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"} hover:bg-blue-50/30`}>
                <td className="px-3 py-1.5 text-xs font-mono text-gray-400">{d.draw_number}</td>
                <td className="px-3 py-1.5 text-xs text-gray-600">{d.draw_date}</td>
                {d.balls.map((b, bi) => (
                  <td key={bi} className="px-3 py-1.5 text-center">
                    <span className="inline-flex w-7 h-7 rounded-full bg-[#002FA7] text-white items-center justify-center text-xs font-bold">{b}</span>
                  </td>
                ))}
                {d.lucky_stars.map((ls, li) => (
                  <td key={li} className="px-3 py-1.5 text-center">
                    <span className="inline-flex w-7 h-7 rounded-full bg-amber-500 text-white items-center justify-center text-xs font-bold">{ls}</span>
                  </td>
                ))}
                {diffKeys.map(dk => {
                  const v = d.diffs[dk.key];
                  const c = DIFF_COLORS[dk.key];
                  return (
                    <td key={dk.key} className="px-3 py-1.5 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded font-mono font-bold text-xs ${c.bg} ${c.text} border ${c.border}`}>
                        {v !== null ? (v > 0 ? `+${v}` : v) : "-"}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </Block>
  );
}

function ComparisonView({ stats2026, statsHist, deviation, diffKeys }) {
  return (
    <Block
      n={2}
      title="Statistical Comparison — 2026 vs Historical (22 Years)"
      subtitle="Per-metric (min, avg, median, mode, max, SD, percentiles) comparison for each adjacent-ball difference. Red = 2026 higher, Blue = 2026 lower."
      testId="w59-block-2"
    >
      <div className="space-y-6" data-testid="comparison-view">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="px-3 py-2 text-left text-[10px] font-bold text-gray-500 uppercase w-20">Metric</th>
                {diffKeys.map(dk => (
                  <th key={dk.key} className="text-center" colSpan={2}>
                    <span className={`text-[10px] font-bold uppercase ${DIFF_COLORS[dk.key].text}`}>{dk.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {["min", "avg", "median", "mode", "max", "std_dev", "p5", "p25", "p75", "p95"].map(metric => (
                <tr key={metric} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-3 py-1.5 text-xs font-bold text-gray-600 uppercase">{metric === "std_dev" ? "SD" : metric}</td>
                  {diffKeys.map(dk => {
                    const s26 = stats2026[dk.key] || {};
                    const sh = statsHist[dk.key] || {};
                    const v26 = s26[metric];
                    const vh = sh[metric];
                    const isDiff = v26 !== undefined && vh !== undefined && v26 !== vh;
                    const isHigher = v26 > vh;
                    return (
                      <td key={dk.key} className="text-center px-1 py-1.5" colSpan={2}>
                        <div className="flex items-center justify-center gap-2">
                          <span className={`font-mono text-xs font-bold ${isDiff && isHigher ? "text-red-600" : isDiff ? "text-blue-600" : "text-gray-700"}`}>
                            {v26 ?? "-"}
                          </span>
                          <span className="text-[9px] text-gray-300">/</span>
                          <span className="font-mono text-xs text-gray-400">{vh ?? "-"}</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-2 text-xs font-black text-gray-700 uppercase">Signal</td>
                {diffKeys.map(dk => {
                  const dev = deviation[dk.key] || {};
                  const signalColor = (dev.signal || "").includes("VERY") ? "bg-red-100 text-red-700" : (dev.signal || "").includes("HIGH") || (dev.signal || "").includes("LOW") ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700";
                  return (
                    <td key={dk.key} className="text-center px-1 py-2" colSpan={2}>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded ${signalColor}`}>
                        {dev.signal || "N/A"} (Z:{dev.z_score ?? "-"})
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-400">
          <span className="font-bold text-gray-600">2026</span> <span className="text-gray-300">/</span> <span className="text-gray-400">Historical</span> &nbsp;|&nbsp;
          <span className="text-red-600">Red</span> = 2026 higher &nbsp;|&nbsp; <span className="text-blue-600">Blue</span> = 2026 lower
        </div>
      </div>
      </div>
    </Block>
  );
}

function TrendsView({ trends, diffKeys, statsHist }) {
  const [patternData, setPatternData] = useState(null);
  const [patternLoading, setPatternLoading] = useState(true);
  const [patternLen, setPatternLen] = useState(5);
  const [tolerance, setTolerance] = useState(2);
  const [pctTolerance, setPctTolerance] = useState(15);

  useEffect(() => {
    const load = () => {
      setPatternLoading(true);
      axios.get(`${API}/api/analysis/ball-diff-pattern-match?pattern_len=${patternLen}&tolerance=${tolerance}&pct_tolerance=${pctTolerance}`)
        .then(res => setPatternData(res.data))
        .catch(() => setPatternData(null))
        .finally(() => setPatternLoading(false));
    };
    load();
    const silent = () => {
      axios.get(`${API}/api/analysis/ball-diff-pattern-match?pattern_len=${patternLen}&tolerance=${tolerance}&pct_tolerance=${pctTolerance}`)
        .then(res => setPatternData(res.data))
        .catch(() => {});
    };
    const onEvt = () => silent();
    const onStorage = (e) => { if (e.key === 'euromillions:last_draw_update') silent(); };
    window.addEventListener('euromillions:draws-updated', onEvt);
    window.addEventListener('storage', onStorage);
    let bc;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        bc = new BroadcastChannel('euromillions');
        bc.onmessage = (ev) => { if (ev.data === 'draws-updated') silent(); };
      }
    } catch {}
    return () => {
      window.removeEventListener('euromillions:draws-updated', onEvt);
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [patternLen, tolerance, pctTolerance]);

  return (
    <div className="space-y-5" data-testid="trends-view">
      {/* Block 2 — Pattern-Match Predictor */}
      <Block
        n={2}
        title="Pattern Match — What Happened Next in History?"
        subtitle="For each adjacent-ball difference: find historical sequences matching the recent N-value pattern (±tolerance) and aggregate what came next."
        testId="w59-block-2"
      >
        <div className="flex items-center gap-4 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-indigo-900 uppercase">Pattern length:</label>
            <select
              value={patternLen}
              onChange={e => setPatternLen(parseInt(e.target.value))}
              className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1"
              data-testid="pattern-len-select"
            >
              {[3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n} draws</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-indigo-900 uppercase">Actual tol:</label>
            <select
              value={tolerance}
              onChange={e => setTolerance(parseInt(e.target.value))}
              className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1"
              data-testid="tolerance-select"
            >
              {[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>±{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-indigo-900 uppercase">Adaptive ±%:</label>
            <select
              value={pctTolerance}
              onChange={e => setPctTolerance(parseInt(e.target.value))}
              className="text-xs font-mono font-bold bg-white border border-indigo-300 rounded px-2 py-1"
              data-testid="pct-tolerance-select"
            >
              {[10, 15, 20, 25, 30, 40, 50].map(n => <option key={n} value={n}>±{n}%</option>)}
            </select>
          </div>
          <div className="text-[10px] text-indigo-700 italic ml-auto max-w-md">
            <b>Actual</b> = fixed ±N integer match. <b>Adaptive</b> = ±% per-value (floor ±1). Agreement banner compares both.
          </div>
        </div>

        {patternLoading ? (
          <div className="flex items-center justify-center py-10">
            <CircleNotch size={24} className="animate-spin text-indigo-600" />
            <span className="ml-2 text-sm text-gray-500">Scanning history for matching patterns...</span>
          </div>
        ) : !patternData || patternData.error ? (
          <div className="text-center text-gray-500 py-10">No pattern data available.</div>
        ) : (
          <div className="space-y-4">
            {diffKeys.map(dk => {
              const p = (patternData.per_diff || {})[dk.key];
              if (!p) return null;
              const c = DIFF_COLORS[dk.key];
              const a = p.actual || {};
              const ad = p.adaptive || {};
              const ag = p.agreement || {};
              const agStyle = ag.level === 'STRONG' ? 'bg-emerald-100 border-emerald-400 text-emerald-900'
                : ag.level === 'MODERATE' ? 'bg-blue-100 border-blue-400 text-blue-900'
                : ag.level === 'PARTIAL' ? 'bg-amber-100 border-amber-400 text-amber-900'
                : ag.level === 'WEAK' ? 'bg-rose-100 border-rose-400 text-rose-900'
                : 'bg-gray-100 border-gray-400 text-gray-700';

              return (
                <div key={dk.key} className={`${c.bg} border-2 ${c.border} rounded-lg p-3`} data-testid={`pattern-${dk.key}`}>
                  {/* Header: pattern visualization + counts */}
                  <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm ${c.text}`}>{dk.label}</span>
                      <span className="text-[10px] text-gray-500">Pattern (last {patternLen} draws):</span>
                      <div className="flex items-center gap-1">
                        {(p.pattern || []).map((v, i) => (
                          <span key={i} className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded ${i === p.pattern.length - 1 ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-white border border-gray-300 text-gray-700'}`}>
                            {v > 0 ? `+${v}` : v}
                          </span>
                        ))}
                        <span className="text-gray-400 mx-1">→</span>
                        <span className="font-mono font-bold text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-400 ring-1 ring-dashed ring-gray-400">?</span>
                      </div>
                    </div>
                  </div>

                  {/* Agreement banner */}
                  <div className={`border-l-4 px-3 py-1.5 rounded text-xs font-bold mb-3 ${agStyle}`} data-testid={`agreement-${dk.key}`}>
                    {ag.label}
                    {ag.direction_drift !== undefined && ag.level !== 'NONE' && ag.level !== 'PARTIAL' && (
                      <span className="ml-2 text-[10px] font-normal opacity-70">(direction drift: {ag.direction_drift} pp)</span>
                    )}
                  </div>

                  {/* CONSENSUS / FINAL VERDICT — shown above everything else */}
                  {p.consensus && (
                    <div className={`mb-3 rounded-lg p-3 border-2 ${
                      p.consensus.side_label === 'UP' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400' :
                      p.consensus.side_label === 'DOWN' ? 'bg-gradient-to-br from-rose-50 to-pink-50 border-rose-400' :
                      p.consensus.side_label === 'MIXED' ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-400' :
                      'bg-gray-50 border-gray-300'
                    }`} data-testid={`consensus-${dk.key}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Result / Probability</span>
                            <span className="text-[9px] text-gray-400 italic">{p.consensus.formula_used}</span>
                          </div>
                          <div className={`text-xl font-black mt-1 ${
                            p.consensus.side_label === 'UP' ? 'text-emerald-800' :
                            p.consensus.side_label === 'DOWN' ? 'text-rose-800' :
                            p.consensus.side_label === 'MIXED' ? 'text-amber-800' :
                            'text-gray-800'
                          }`} data-testid={`final-verdict-${dk.key}`}>
                            {p.consensus.final_verdict}
                          </div>
                          {/* Tier / Reliability badge */}
                          {p.consensus.tier && (
                            <div className={`mt-1 inline-block px-2 py-0.5 rounded text-[11px] font-bold ${
                              p.consensus.tier === 'ELITE' ? 'bg-purple-600 text-white' :
                              p.consensus.tier === 'STRONG' ? 'bg-emerald-600 text-white' :
                              p.consensus.tier === 'NORMAL' ? 'bg-blue-500 text-white' :
                              p.consensus.tier === 'WIDE' ? 'bg-orange-500 text-white' :
                              'bg-gray-400 text-white'
                            }`} data-testid={`tier-${dk.key}`}>
                              {p.consensus.tier_label}
                              <span className="ml-1 opacity-80 font-normal">· {p.consensus.used_matches_total}m · ±{p.consensus.band_pct}%</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right text-[10px] text-gray-600">
                          <div>
                            <span title="Raw % of scales agreeing on direction, adjusted down for small sample">Direction conf:</span>{' '}
                            <b className={`${
                              p.consensus.tier === 'ELITE' ? 'text-purple-700' :
                              p.consensus.tier === 'STRONG' ? 'text-emerald-700' :
                              p.consensus.tier === 'NORMAL' ? 'text-blue-700' :
                              p.consensus.tier === 'WIDE' ? 'text-orange-700' :
                              'text-gray-500'
                            }`}>{p.consensus.effective_confidence_pct}%</b>{' '}
                            <span className="text-gray-300 text-[9px]" title={`Raw ${p.consensus.direction_confidence_pct}% × tier multiplier ${p.consensus.tier_multiplier}`}>
                              (raw {p.consensus.direction_confidence_pct}%)
                            </span>
                          </div>
                          <div>Mode consensus: <b className="text-gray-900 font-mono">{p.consensus.mode_consensus !== null && p.consensus.mode_consensus !== undefined ? (p.consensus.mode_consensus > 0 ? `+${p.consensus.mode_consensus}` : p.consensus.mode_consensus) : '-'}</b></div>
                          <div>Sample: <b className="text-gray-900">{p.consensus.used_matches_total}m</b> · Band <b className="text-gray-900">±{p.consensus.band_pct}%</b></div>
                        </div>
                      </div>
                      {/* Numeric components row */}
                      <div className="mt-2 pt-2 border-t border-dashed border-current opacity-80 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px]">
                        <span><span className="text-emerald-700 font-bold">wAvgP:</span> <b>{p.consensus.weighted_avg_p ?? '-'}</b></span>
                        <span><span className="text-rose-700 font-bold">wAvgN:</span> <b>{p.consensus.weighted_avg_n ?? '-'}</b></span>
                        <span><span className="text-gray-700 font-bold">wAvgNext:</span> <b>{p.consensus.weighted_avg_next ?? '-'}</b></span>
                        <span className="text-gray-500">· Used: {p.consensus.scales_used.join(', ')}</span>
                        {p.consensus.scales_excluded.length > 0 && (
                          <span className="text-gray-400 italic">· Excluded (n&lt;3): {p.consensus.scales_excluded.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fallback banner + multi-scale matrix shown ABOVE the cards */}
                  {p.fallback_triggered && (
                    <div className="mb-3 bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-lg p-3" data-testid={`fallback-${dk.key}`}>
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs uppercase tracking-wider text-orange-900">⚡ Fallback Analysis — Progressive Backoff</span>
                          <span className="text-[10px] text-orange-700 italic">{p.fallback_reason}</span>
                        </div>
                        {p.multi_scale_verdict && (
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                            p.multi_scale_verdict.level === 'SUPER_STRONG' ? 'bg-emerald-600 text-white' :
                            p.multi_scale_verdict.level === 'STRONG' ? 'bg-emerald-500 text-white' :
                            p.multi_scale_verdict.level === 'MODERATE' ? 'bg-amber-400 text-white' :
                            p.multi_scale_verdict.level === 'CONFLICTING' ? 'bg-orange-500 text-white' :
                            'bg-gray-400 text-white'
                          }`} data-testid={`multi-scale-${dk.key}`}>
                            {p.multi_scale_verdict.label}
                          </span>
                        )}
                      </div>
                      {p.multi_scale && (
                        <div className="bg-white border border-orange-200 rounded overflow-hidden">
                          <table className="w-full text-[10px]">
                            <thead className="bg-orange-100 text-orange-900">
                              <tr>
                                <th className="px-2 py-1 text-left font-bold">Scale</th>
                                <th className="px-2 py-1 text-left font-bold">Mode</th>
                                <th className="px-2 py-1 text-right font-bold">Matches</th>
                                <th className="px-2 py-1 text-center font-bold">Direction</th>
                                <th className="px-2 py-1 text-left font-bold">Top Next</th>
                                <th className="px-2 py-1 text-right font-bold" title="Average of all next-values">Avg</th>
                                <th className="px-2 py-1 text-right font-bold text-emerald-700" title="# matches where next > last (UP)">Pos</th>
                                <th className="px-2 py-1 text-right font-bold text-emerald-600" title="Avg of positive next-values">AvgP</th>
                                <th className="px-2 py-1 text-right font-bold text-rose-700" title="# matches where next < last (DOWN)">Neg</th>
                                <th className="px-2 py-1 text-right font-bold text-rose-600" title="Avg of negative next-values">AvgN</th>
                                <th className="px-2 py-1 text-right font-bold text-amber-700" title="# matches where next == last (FLAT)">Flat</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.multi_scale.map((s, i) => (
                                <tr key={i} className={`border-t border-orange-100 ${s.matches < 3 ? 'opacity-60' : ''}`}>
                                  <td className="px-2 py-1 font-mono font-bold">len {s.len}</td>
                                  <td className="px-2 py-1 text-gray-600">{s.mode}</td>
                                  <td className="px-2 py-1 text-right font-mono font-bold">{s.matches}</td>
                                  <td className="px-2 py-1 text-center">
                                    {s.dominant ? (
                                      <span className={`font-bold text-[10px] px-1.5 py-0.5 rounded ${
                                        s.dominant === 'up' ? 'bg-emerald-100 text-emerald-800' :
                                        s.dominant === 'down' ? 'bg-rose-100 text-rose-800' :
                                        'bg-amber-100 text-amber-800'
                                      }`}>
                                        {s.dominant === 'up' ? '↑ UP' : s.dominant === 'down' ? '↓ DOWN' : '→ FLAT'}
                                      </span>
                                    ) : <span className="text-gray-300 text-[9px] italic">&lt;3 m</span>}
                                  </td>
                                  <td className="px-2 py-1 font-mono text-[10px]" title="top 3 next-values with counts">
                                    {s.top3_summary && s.top3_summary.length > 0 ? s.top3_summary.join(', ') : '-'}
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono font-bold">{s.avg_next !== null && s.avg_next !== undefined ? s.avg_next : '-'}</td>
                                  <td className="px-2 py-1 text-right font-mono text-emerald-700">{s.n_positive || '-'}</td>
                                  <td className="px-2 py-1 text-right font-mono text-emerald-600">{s.avg_positive !== null && s.avg_positive !== undefined ? s.avg_positive : '-'}</td>
                                  <td className="px-2 py-1 text-right font-mono text-rose-700">{s.n_negative || '-'}</td>
                                  <td className="px-2 py-1 text-right font-mono text-rose-600">{s.avg_negative !== null && s.avg_negative !== undefined ? s.avg_negative : '-'}</td>
                                  <td className="px-2 py-1 text-right font-mono text-amber-700">{s.n_flat || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* All comparison cards in a single grid (2 when no fallback, 4 when fallback) */}
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${p.fallback_triggered ? 'lg:grid-cols-4' : ''} gap-3`}>
                    {[
                      { label: 'Actual', data: a, subtitle: `len ${p.pattern_len} · ±${a.tolerance} fixed`, accent: 'border-slate-400', testId: `actual-${dk.key}` },
                      { label: 'Adaptive', data: ad, subtitle: `len ${p.pattern_len} · ±${ad.pct_tolerance}% (pos: ${(ad.position_tolerances || []).map(t => `±${t}`).join(' ')})`, accent: 'border-indigo-400', testId: `adaptive-${dk.key}` },
                      ...(p.fallback_triggered && p.tier2 ? Object.entries(p.tier2).map(([lkey, sub]) => ({
                        label: `Len ${sub.pattern_len} Adaptive`,
                        data: sub,
                        subtitle: '±' + sub.pct_tolerance + '% · ' + (sub.pattern || []).map(v => v > 0 ? '+' + v : v).join(' '),
                        accent: 'border-orange-400',
                        testId: `tier2-${dk.key}-${lkey}`,
                      })) : []),
                    ].map((mode, idx) => {
                      const d = mode.data || {};
                      const lowSample = d.total_matches > 0 && d.total_matches < 10;
                      const noMatch = d.total_matches === 0;
                      return (
                        <div key={idx} className={`bg-white border-l-4 ${mode.accent} rounded p-2.5`} data-testid={mode.testId}>
                          <div className="flex items-baseline justify-between mb-1.5 gap-2">
                            <div className="min-w-0">
                              <span className="font-bold text-xs uppercase tracking-wider text-gray-700">{mode.label}</span>
                              <div className="text-[9px] text-gray-400 font-mono truncate">{mode.subtitle}</div>
                            </div>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${
                              noMatch ? 'bg-gray-200 text-gray-500' :
                              lowSample ? 'bg-amber-100 text-amber-800' :
                              d.total_matches < 30 ? 'bg-blue-100 text-blue-800' :
                              'bg-emerald-100 text-emerald-800'
                            }`}>
                              {d.total_matches}m {noMatch ? '⚫' : lowSample ? '🟡' : d.total_matches < 30 ? '🔵' : '🟢'}
                            </span>
                          </div>

                          {noMatch ? (
                            <div className="text-[10px] text-gray-500 italic py-2">No historical precedent.</div>
                          ) : (
                            <>
                              <div className="text-[10px] text-gray-700 mb-1.5 bg-gray-50 px-2 py-1 rounded">{d.verdict}</div>

                              <div className="flex items-center gap-0.5 h-3 rounded-full overflow-hidden bg-gray-100 mb-2 text-[8px] font-bold text-white">
                                <div className="bg-emerald-500 flex items-center justify-center" style={{ width: `${d.direction_breakdown.up_pct}%` }}>
                                  {d.direction_breakdown.up_pct >= 15 ? `↑${d.direction_breakdown.up_pct}%` : ''}
                                </div>
                                <div className="bg-amber-400 flex items-center justify-center" style={{ width: `${d.direction_breakdown.flat_pct}%` }}>
                                  {d.direction_breakdown.flat_pct >= 15 ? `→${d.direction_breakdown.flat_pct}%` : ''}
                                </div>
                                <div className="bg-rose-500 flex items-center justify-center" style={{ width: `${d.direction_breakdown.down_pct}%` }}>
                                  {d.direction_breakdown.down_pct >= 15 ? `↓${d.direction_breakdown.down_pct}%` : ''}
                                </div>
                              </div>

                              <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                {(d.distribution || []).slice(0, 5).map((row, i) => (
                                  <div key={i} className="flex items-center gap-1.5 text-[10px]">
                                    <span className={`font-mono font-bold w-9 text-right ${row.direction === 'up' ? 'text-emerald-700' : row.direction === 'down' ? 'text-rose-700' : 'text-amber-700'}`}>
                                      {row.value > 0 ? `+${row.value}` : row.value}
                                    </span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                      <div className={`h-full ${row.direction === 'up' ? 'bg-emerald-400' : row.direction === 'down' ? 'bg-rose-400' : 'bg-amber-400'}`} style={{ width: `${Math.min(row.pct, 100)}%` }} />
                                    </div>
                                    <span className="font-mono font-bold text-gray-600 w-10 text-right">{row.pct}%</span>
                                    <span className="text-gray-400 w-6 text-right">({row.count})</span>
                                  </div>
                                ))}
                              </div>

                              <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex flex-wrap gap-x-2 gap-y-0.5 text-[9px] text-gray-500">
                                <span>Avg: <b className="text-gray-800">{d.avg_next}</b></span>
                                <span>Med: <b className="text-gray-800">{d.stats.median}</b></span>
                                <span className="text-emerald-600">+{d.n_positive}({d.avg_positive ?? '-'})</span>
                                <span className="text-rose-600">−{d.n_negative}({d.avg_negative ?? '-'})</span>
                                {d.n_flat > 0 && <span className="text-amber-600">={d.n_flat}</span>}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </Block>

      {/* Block 3 — 2026 Trends Timeline (moved from Block 2) */}
    <Block
      n={3}
      title="2026 Trends — Per-Diff Timeline, 5-Draw MA & Valley Recovery"
      subtitle="For each adjacent-ball difference: 2026 trendline vs historical average, peak/valley detection, avg valley-to-peak recovery time & jump."
      testId="w59-block-3"
    >
      <div className="space-y-4">
        {diffKeys.map(dk => {
          const t = trends[dk.key];
          if (!t || t.length === 0) return null;
          const c = DIFF_COLORS[dk.key];
          const values = t.map(x => x.value);
          const minV = Math.min(...values);
          const maxV = Math.max(...values);
          const range = maxV - minV || 1;
          const chartH = 160;

          const sh = statsHist[dk.key] || {};
          const histAvg = sh.avg || null;
        const avg2026 = values.length ? +(values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) : 0;

        // Peak/Valley analysis
        const peaks = [];
        const valleys = [];
        for (let i = 1; i < values.length - 1; i++) {
          if (values[i] > values[i - 1] && values[i] >= values[i + 1]) peaks.push({ idx: i, val: values[i] });
          if (values[i] < values[i - 1] && values[i] <= values[i + 1]) valleys.push({ idx: i, val: values[i] });
        }

        // Valley-to-peak recovery: after each valley, how many draws to next peak?
        const recoveries = [];
        for (const v of valleys) {
          const nextPeak = peaks.find(p => p.idx > v.idx);
          if (nextPeak) {
            recoveries.push({ draws: nextPeak.idx - v.idx, from: v.val, to: nextPeak.val, jump: nextPeak.val - v.val });
          }
        }
        const avgRecoveryDraws = recoveries.length ? +(recoveries.reduce((a, r) => a + r.draws, 0) / recoveries.length).toFixed(1) : null;
        const avgRecoveryJump = recoveries.length ? +(recoveries.reduce((a, r) => a + r.jump, 0) / recoveries.length).toFixed(1) : null;

        // Is current value at a valley?
        const lastVal = values[values.length - 1];
        const prevVal = values.length > 1 ? values[values.length - 2] : lastVal;
        const isAtValley = lastVal <= prevVal && lastVal <= (values.length > 2 ? values[values.length - 3] : lastVal + 1);
        const isAtPeak = lastVal >= prevVal && lastVal >= (values.length > 2 ? values[values.length - 3] : lastVal - 1);

        // Gap from hist avg
        const gapFromHist = histAvg !== null ? +(avg2026 - histAvg).toFixed(2) : null;
        const gapPct = histAvg ? +((gapFromHist / histAvg) * 100).toFixed(1) : null;

        const points = values.map((v, i) => {
          const x = (i / Math.max(values.length - 1, 1)) * 100;
          const y = 100 - ((v - minV) / range) * 100;
          return `${x},${y}`;
        }).join(" ");

        const ma = values.map((_, i) => {
          const start = Math.max(0, i - 4);
          const slice = values.slice(start, i + 1);
          return slice.reduce((a, b) => a + b, 0) / slice.length;
        });
        const maPoints = ma.map((v, i) => {
          const x = (i / Math.max(ma.length - 1, 1)) * 100;
          const y = 100 - ((v - minV) / range) * 100;
          return `${x},${y}`;
        }).join(" ");

        const histAvgY = histAvg !== null ? 100 - ((histAvg - minV) / range) * 100 : null;

        return (
          <div key={dk.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className={`px-4 py-2 ${c.bg} border-b ${c.border} flex items-center justify-between`}>
              <span className={`font-bold text-sm ${c.text}`}>{dk.label} — 2026 Trend</span>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded" style={{ backgroundColor: c.line }} /> Values</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded bg-gray-400" /> 5-Draw MA</span>
                {histAvg !== null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 rounded bg-red-400" /> Hist Avg ({histAvg})</span>}
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Chart - takes 2 cols */}
                <div className="lg:col-span-2">
                  <div className="relative" style={{ height: chartH }}>
                    <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[9px] text-gray-400 font-mono w-6">
                      <span>{maxV}</span>
                      <span>{Math.round((maxV + minV) / 2)}</span>
                      <span>{minV}</span>
                    </div>
                    {histAvgY !== null && histAvgY >= 0 && histAvgY <= 100 && (
                      <div className="absolute left-7 right-0 border-t-2 border-dashed border-red-400/50" style={{ top: `${histAvgY}%` }} />
                    )}
                    <svg className="absolute left-7 right-0 top-0 h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <polyline points={maPoints} fill="none" stroke="#9ca3af" strokeWidth="0.8" strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
                      <polyline points={points} fill="none" stroke={c.line} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
                      {values.map((v, i) => {
                        const x = (i / Math.max(values.length - 1, 1)) * 100;
                        const y = 100 - ((v - minV) / range) * 100;
                        return <circle key={i} cx={x} cy={y} r="1" fill={c.line} />;
                      })}
                    </svg>
                  </div>
                  <div className="ml-7 flex justify-between mt-1 text-[8px] text-gray-400 font-mono">
                    {t.length > 0 && <span>#{t[0].draw_number}</span>}
                    {t.length > 2 && <span>#{t[Math.floor(t.length / 2)].draw_number}</span>}
                    {t.length > 1 && <span>#{t[t.length - 1].draw_number}</span>}
                  </div>
                  <div className="mt-3 flex gap-2 items-center">
                    <span className="text-[10px] text-gray-500">Last 5:</span>
                    {values.slice(-5).map((v, i) => (
                      <span key={i} className={`font-mono font-bold text-xs px-1.5 py-0.5 rounded ${c.bg} ${c.text}`}>
                        {v > 0 ? `+${v}` : v}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Insight Panel */}
                <div className="space-y-2.5">
                  {/* Avg comparison */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Average Comparison</div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-xl font-black ${gapFromHist !== null && gapFromHist < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{avg2026}</span>
                      <span className="text-xs text-gray-400">2026</span>
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                      <span className="text-sm font-bold text-gray-600">{histAvg || '-'}</span>
                      <span className="text-xs text-gray-400">Historical</span>
                    </div>
                    {gapFromHist !== null && (
                      <div className={`mt-1.5 text-[10px] font-bold ${gapFromHist < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {gapFromHist > 0 ? '+' : ''}{gapFromHist} ({gapPct > 0 ? '+' : ''}{gapPct}%)
                        {gapFromHist < -0.5 && <span className="text-red-500 ml-1">Higher diff likely</span>}
                        {gapFromHist > 0.5 && <span className="text-emerald-500 ml-1">Running above avg</span>}
                      </div>
                    )}
                  </div>

                  {/* Peak/Valley */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Peak & Valley</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-gray-400">Max Peak:</span> <span className="font-bold text-gray-900">{maxV}</span></div>
                      <div><span className="text-gray-400">Min Valley:</span> <span className="font-bold text-gray-900">{minV}</span></div>
                      <div><span className="text-gray-400">Range:</span> <span className="font-bold text-gray-900">{maxV - minV}</span></div>
                      <div><span className="text-gray-400">Peaks:</span> <span className="font-bold">{peaks.length}</span> <span className="text-gray-400">Valleys:</span> <span className="font-bold">{valleys.length}</span></div>
                    </div>
                  </div>

                  {/* Recovery pattern */}
                  {recoveries.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Valley Recovery</div>
                      <div className="text-xs text-gray-700 space-y-1">
                        <div>Avg draws to recover: <span className="font-bold text-indigo-700">{avgRecoveryDraws}</span></div>
                        <div>Avg recovery jump: <span className="font-bold text-indigo-700">+{avgRecoveryJump}</span></div>
                        <div className="text-[10px] text-gray-400">{recoveries.length} valley-to-peak cycles in 2026</div>
                      </div>
                    </div>
                  )}

                  {/* Current position */}
                  <div className={`rounded-lg p-3 ${isAtValley ? 'bg-amber-50 border border-amber-200' : isAtPeak ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50'}`}>
                    <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Current Position</div>
                    <div className="text-xs">
                      <span className="font-bold text-gray-900">Last: {lastVal}</span>
                      {isAtValley && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">AT VALLEY — bounce likely</span>}
                      {isAtPeak && <span className="ml-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">AT PEAK</span>}
                      {!isAtValley && !isAtPeak && <span className="ml-2 text-[10px] text-gray-400">Mid-range</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      </div>
    </Block>
    </div>
  );
}


const GAP_LABELS = { b2_b1: "B2-B1", b3_b2: "B3-B2", b4_b3: "B4-B3", b5_b4: "B5-B4" };
const GAP_COLORS = {
  b2_b1: { bg: "bg-blue-100", text: "text-blue-800", bar: "#3b82f6", border: "border-blue-300" },
  b3_b2: { bg: "bg-emerald-100", text: "text-emerald-800", bar: "#10b981", border: "border-emerald-300" },
  b4_b3: { bg: "bg-amber-100", text: "text-amber-800", bar: "#f59e0b", border: "border-amber-300" },
  b5_b4: { bg: "bg-rose-100", text: "text-rose-800", bar: "#f43f5e", border: "border-rose-300" },
};

function TwinYearView({ data, loading }) {
  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <CircleNotch size={32} className="animate-spin text-blue-600" />
      <span className="ml-3 text-sm text-gray-500">Computing year similarities...</span>
    </div>
  );
  if (!data) return <div className="text-center text-gray-500 py-20">No data available</div>;

  const { profile_2026, top_matches_full, top_matches_q1, gap_migration, q2_projection, q1_2026_gap_ranking } = data;
  const gapKeys = ["b2_b1", "b3_b2", "b4_b3", "b5_b4"];

  return (
    <Block
      n={2}
      title="Twin Year — Historical Year Matching & Q1/Q2 Projection"
      subtitle="Find historical years with the most similar 2026 structural DNA (sums, spreads, odds, highs). Projects Q2 gap behaviour from matched years."
      testId="w59-block-2"
    >
    <div className="space-y-5" data-testid="twin-year-view">
      {/* 2026 DNA profile card */}
      <div className="bg-white border-2 border-gray-800 rounded-lg overflow-hidden">
        <div className="bg-gray-900 px-4 py-2.5 flex items-center justify-between">
          <h3 className="text-sm font-black text-white uppercase tracking-wider">2026 Structural DNA</h3>
          <span className="text-[10px] text-gray-400">{profile_2026?.draws} draws analyzed</span>
        </div>
        <div className="p-4 grid grid-cols-6 gap-3">
          {[
            { label: "Avg Sum", value: profile_2026?.avg_sum },
            { label: "Avg Spread", value: profile_2026?.avg_spread },
            { label: "Avg Odds/Draw", value: profile_2026?.avg_odds },
            { label: "Avg High/Draw", value: profile_2026?.avg_high },
            { label: "Avg Primes", value: profile_2026?.avg_primes },
            { label: "Avg Boundary", value: profile_2026?.avg_boundary },
            { label: "Avg Round(0/5)", value: profile_2026?.avg_round },
            { label: "Avg Consec", value: profile_2026?.avg_consecutive },
            { label: "Avg Mult3", value: profile_2026?.avg_mult3 },
            { label: "Avg LS Sum", value: profile_2026?.avg_ls_sum },
            { label: "O/E Mode", value: `${profile_2026?.oe_mode}O/${5 - (profile_2026?.oe_mode || 0)}E` },
            { label: "H/L Mode", value: `${profile_2026?.hl_mode}H/${5 - (profile_2026?.hl_mode || 0)}L` },
          ].map((m, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-2.5 text-center">
              <div className="text-[9px] text-gray-400 uppercase font-bold tracking-wider">{m.label}</div>
              <div className="font-mono font-bold text-lg text-gray-900">{m.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 2026 Q1 Gap Ranking */}
      {q1_2026_gap_ranking && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-gray-200">
            <h3 className="font-bold text-sm text-gray-800">2026 Q1 Gap Ranking</h3>
            <p className="text-[10px] text-gray-500">Current gap dominance — which position has the widest average gap?</p>
          </div>
          <div className="p-4">
            <div className="flex gap-3">
              {(q1_2026_gap_ranking.ranked || []).map((r, i) => {
                const c = GAP_COLORS[r.pos] || GAP_COLORS.b2_b1;
                const maxG = q1_2026_gap_ranking.ranked[0]?.avg_gap || 1;
                const barW = Math.round((r.avg_gap / maxG) * 100);
                return (
                  <div key={r.pos} className={`flex-1 ${c.bg} border ${c.border} rounded-lg p-3`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-black ${c.text} uppercase`}>{GAP_LABELS[r.pos]}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${i === 0 ? "bg-red-600 text-white" : "bg-gray-200 text-gray-600"}`}>
                        {i === 0 ? "HIGHEST" : `#${i + 1}`}
                      </span>
                    </div>
                    <div className="font-mono font-bold text-2xl text-gray-900">{r.avg_gap}</div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${barW}%`, backgroundColor: c.bar }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Top Twin Year Matches — Full Year */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="twin-matches-full">
        <div className="px-4 py-2.5 bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-200">
          <h3 className="font-bold text-sm text-gray-800">Top Twin Years (Full-Year DNA Match)</h3>
          <p className="text-[10px] text-gray-500">Which historical year had the most similar structural profile to 2026?</p>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {(top_matches_full || []).map((m, i) => {
              const p = m.profile || {};
              const maxScore = top_matches_full[top_matches_full.length - 1]?.score || 1;
              const similarity = Math.max(0, Math.round(100 - (m.score / Math.max(maxScore, 1)) * 50));
              return (
                <div key={m.year} className={`border rounded-lg overflow-hidden ${i === 0 ? "border-blue-400 bg-blue-50/30" : "border-gray-200"}`}>
                  <div className="flex items-center gap-4 px-4 py-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-lg ${i === 0 ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}>
                      #{i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-xl text-gray-900">{m.year}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-gray-500">{m.draws} draws</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold ${m.score <= 5 ? "bg-green-100 text-green-700" : m.score <= 10 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          Dist: {m.score}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-1 text-[10px] text-gray-500">
                        <span>Sum: <b className="text-gray-700">{p.avg_sum}</b></span>
                        <span>Spread: <b className="text-gray-700">{p.avg_spread}</b></span>
                        <span>Odds: <b className="text-gray-700">{p.avg_odds}</b></span>
                        <span>High: <b className="text-gray-700">{p.avg_high}</b></span>
                        <span>Primes: <b className="text-gray-700">{p.avg_primes}</b></span>
                        <span>LS Sum: <b className="text-gray-700">{p.avg_ls_sum}</b></span>
                        <span>O/E: <b className="text-gray-700">{p.oe_mode}O/{5 - p.oe_mode}E</b></span>
                        <span>H/L: <b className="text-gray-700">{p.hl_mode}H/{5 - p.hl_mode}L</b></span>
                      </div>
                    </div>
                    {/* Gap profile mini-bars */}
                    <div className="flex gap-1">
                      {gapKeys.map(gk => {
                        const gVal = p.gap_avgs?.[gk] || 0;
                        const maxG = Math.max(...gapKeys.map(k => p.gap_avgs?.[k] || 0), 1);
                        const h = Math.max(8, Math.round((gVal / maxG) * 40));
                        const isMax = gk === p.max_gap_pos;
                        const gc = GAP_COLORS[gk];
                        return (
                          <div key={gk} className="flex flex-col items-center gap-0.5" style={{ width: 28 }}>
                            <div className="text-[7px] font-mono font-bold text-gray-400">{gVal.toFixed(1)}</div>
                            <div className={`w-4 rounded-t ${isMax ? "ring-2 ring-red-400" : ""}`} style={{ height: h, backgroundColor: gc.bar }} />
                            <div className={`text-[7px] font-bold ${gc.text}`}>{GAP_LABELS[gk]?.replace("-", "")?.slice(0, 4)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Gap Migration — How Q1→Q2 shifted in twin years */}
      {gap_migration && Object.keys(gap_migration).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="gap-migration">
          <div className="px-4 py-2.5 bg-gradient-to-r from-violet-50 to-fuchsia-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ArrowsLeftRight size={16} className="text-violet-600" weight="bold" />
              <div>
                <h3 className="font-bold text-sm text-gray-800">Gap Migration: Q1 → Q2 in Twin Years</h3>
                <p className="text-[10px] text-gray-500">How did the dominant gap position shift from Q1 to Q2 in these years?</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {Object.entries(gap_migration).map(([yr, mg]) => {
              const shift = mg.q1_to_q2_shift;
              const delta = mg.q1_to_q2_delta || {};
              const q1 = mg.quarters?.Q1;
              const q2 = mg.quarters?.Q2;
              if (!q1 || !q2) return null;
              return (
                <div key={yr} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                    <span className="font-bold text-sm text-gray-800">{yr}</span>
                    {shift && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${shift.shifted ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-500"}`}>
                        {shift.shifted
                          ? `Max Gap: ${GAP_LABELS[shift.from]} → ${GAP_LABELS[shift.to]}`
                          : `Max Gap stayed at ${GAP_LABELS[shift.from]}`
                        }
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="grid grid-cols-4 gap-2">
                      {gapKeys.map(gk => {
                        const q1Val = q1.gap_avgs?.[gk] || 0;
                        const q2Val = q2.gap_avgs?.[gk] || 0;
                        const d = delta[gk] || 0;
                        const isShiftTo = shift?.to === gk && shift?.shifted;
                        const isShiftFrom = shift?.from === gk && shift?.shifted;
                        const gc = GAP_COLORS[gk];
                        return (
                          <div key={gk} className={`${gc.bg} border ${gc.border} rounded-lg p-2.5 ${isShiftTo ? "ring-2 ring-violet-500" : ""}`}>
                            <div className={`text-[10px] font-black ${gc.text} uppercase tracking-wider flex items-center justify-between`}>
                              <span>{GAP_LABELS[gk]}</span>
                              {isShiftTo && <span className="text-[8px] px-1 py-0.5 bg-violet-600 text-white rounded">MIGRATED HERE</span>}
                              {isShiftFrom && <span className="text-[8px] px-1 py-0.5 bg-gray-600 text-white rounded">MIGRATED FROM</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="text-center">
                                <div className="text-[8px] text-gray-400">Q1</div>
                                <div className="font-mono font-bold text-sm">{q1Val.toFixed(1)}</div>
                              </div>
                              <ArrowDown size={12} className={`${d > 0.5 ? "text-red-500 rotate-180" : d < -0.5 ? "text-green-500" : "text-gray-400"}`} weight="bold" />
                              <div className="text-center">
                                <div className="text-[8px] text-gray-400">Q2</div>
                                <div className="font-mono font-bold text-sm">{q2Val.toFixed(1)}</div>
                              </div>
                              <span className={`ml-auto text-[10px] font-bold ${d > 0.5 ? "text-red-600" : d < -0.5 ? "text-green-600" : "text-gray-400"}`}>
                                {d > 0 ? "+" : ""}{d.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Q2 Projection */}
      {q2_projection && (
        <div className="bg-white border-2 border-violet-400 rounded-lg overflow-hidden" data-testid="q2-projection">
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3">
            <h3 className="text-sm font-black text-white uppercase tracking-wider">Q2 2026 Projection</h3>
            <p className="text-[10px] text-violet-200">Based on Q2 behavior of top {q2_projection.sources?.length || 0} twin years: {q2_projection.sources?.join(", ")}</p>
          </div>
          <div className="p-4 space-y-4">
            {/* Projected gap profile */}
            <div className="grid grid-cols-4 gap-3">
              {(q2_projection.ranked_positions || []).map((r, i) => {
                const gc = GAP_COLORS[r.pos];
                const maxG = q2_projection.ranked_positions[0]?.avg_gap || 1;
                const barW = Math.round((r.avg_gap / maxG) * 100);
                const q1Val = q1_2026_gap_ranking?.avg_gaps?.[r.pos] || 0;
                const change = r.avg_gap - q1Val;
                return (
                  <div key={r.pos} className={`${gc.bg} border ${gc.border} rounded-lg p-3 ${i === 0 ? "ring-2 ring-violet-500" : ""}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-black ${gc.text} uppercase`}>{GAP_LABELS[r.pos]}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${i === 0 ? "bg-red-600 text-white" : "bg-gray-200 text-gray-600"}`}>
                        {i === 0 ? "PROJECTED MAX" : `#${i + 1}`}
                      </span>
                    </div>
                    <div className="font-mono font-bold text-2xl text-gray-900">{r.avg_gap}</div>
                    <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${barW}%`, backgroundColor: gc.bar }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px]">
                      <span className="text-gray-500">Q1 now: {q1Val.toFixed(1)}</span>
                      <span className={`font-bold ${change > 0.3 ? "text-red-600" : change < -0.3 ? "text-green-600" : "text-gray-400"}`}>
                        {change > 0 ? "+" : ""}{change.toFixed(1)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Migration consensus */}
            {q2_projection.shift_consensus && (
              <div className="bg-violet-50 border border-violet-300 rounded-lg p-3 flex items-center gap-3">
                <TrendUp size={20} weight="bold" className="text-violet-600" />
                <div>
                  <div className="text-xs font-bold text-violet-800">Migration Consensus</div>
                  <div className="text-[11px] text-violet-700 mt-0.5">
                    Based on twin year patterns, the dominant gap is projected to migrate to <b>{GAP_LABELS[q2_projection.shift_consensus[0]]}</b> in Q2
                    {q2_projection.migration_shifts?.length > 0 && (
                      <span className="text-gray-500 ml-1">
                        ({q2_projection.migration_shifts.map(s => `${s.year}: ${GAP_LABELS[s.from]}→${GAP_LABELS[s.to]}`).join(", ")})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Structural projection */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-[9px] text-gray-400 uppercase font-bold">Projected Q2 Avg Sum</div>
                <div className="font-mono font-bold text-xl text-gray-900">{q2_projection.avg_sum}</div>
                <div className="text-[10px] text-gray-500 mt-1">2026 Q1: {profile_2026?.avg_sum}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="text-[9px] text-gray-400 uppercase font-bold">Projected Q2 Avg Spread</div>
                <div className="font-mono font-bold text-xl text-gray-900">{q2_projection.avg_spread}</div>
                <div className="text-[10px] text-gray-500 mt-1">2026 Q1: {profile_2026?.avg_spread}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Q1 Matches */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="twin-matches-q1">
        <div className="px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
          <h3 className="font-bold text-sm text-gray-800">Q1-Specific Twin Years</h3>
          <p className="text-[10px] text-gray-500">Years whose Q1 (Jan-Mar) pattern most closely matches 2026 Q1</p>
        </div>
        <div className="p-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-2 text-[10px] text-gray-400 uppercase">Rank</th>
                <th className="text-left py-2 text-[10px] text-gray-400 uppercase">Year</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">Q1 Draws</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">Distance</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">Sum</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">Spread</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">Odds</th>
                <th className="text-center py-2 text-[10px] text-gray-400 uppercase">High</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-indigo-100 bg-indigo-50/50">
                <td className="py-2 font-bold text-indigo-600">REF</td>
                <td className="py-2 font-bold text-indigo-700">2026 Q1</td>
                <td className="py-2 text-center font-mono">{data.quarterly_2026?.Q1?.draws || "-"}</td>
                <td className="py-2 text-center">—</td>
                <td className="py-2 text-center font-mono font-bold">{data.quarterly_2026?.Q1?.avg_sum || "-"}</td>
                <td className="py-2 text-center font-mono font-bold">{data.quarterly_2026?.Q1?.avg_spread || "-"}</td>
                <td className="py-2 text-center font-mono font-bold">{data.quarterly_2026?.Q1?.avg_odds || "-"}</td>
                <td className="py-2 text-center font-mono font-bold">{data.quarterly_2026?.Q1?.avg_high || "-"}</td>
              </tr>
              {(top_matches_q1 || []).map((m, i) => {
                const qp = m.q1_profile || {};
                return (
                  <tr key={m.year} className={`border-b border-gray-100 ${i === 0 ? "bg-green-50/50" : ""}`}>
                    <td className="py-2 font-bold text-gray-400">#{i + 1}</td>
                    <td className="py-2 font-bold text-gray-800">{m.year}</td>
                    <td className="py-2 text-center font-mono">{m.q1_draws}</td>
                    <td className="py-2 text-center">
                      <span className={`font-bold px-1.5 py-0.5 rounded ${m.score <= 10 ? "bg-green-100 text-green-700" : m.score <= 20 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {m.score}
                      </span>
                    </td>
                    <td className="py-2 text-center font-mono">{qp.avg_sum}</td>
                    <td className="py-2 text-center font-mono">{qp.avg_spread}</td>
                    <td className="py-2 text-center font-mono">{qp.avg_odds}</td>
                    <td className="py-2 text-center font-mono">{qp.avg_high}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </Block>
  );
}
